import maplibregl from 'maplibre-gl';
import BaseProduct from './BaseProduct';
import LayerManager from './LayerManager';
import { ProductId } from '../types';
import store, { getLayerOpacity } from '../state';
import { getTileUrl } from '../config';

export class PrecipAccumLayer extends BaseProduct {
  public readonly id: ProductId = 'precip_accum';
  private unsubscribeStore: (() => void) | null = null;
  private currentMode: 'cumulative' | 'daily' = 'cumulative';
  private currentHours: number = 24;
  private currentDay: number = 1;
  private currentModel: string = 'cmorph2';

  constructor(map: maplibregl.Map, layerManager: LayerManager) {
    super(map, layerManager);

    this.unsubscribeStore = store.subscribe((state) => {
      const modeChanged = this.currentMode !== state.precipAccumMode;
      const hoursChanged = this.currentHours !== state.precipAccumHours;
      const dayChanged = this.currentDay !== state.precipAccumDay;
      const modelChanged = this.currentModel !== state.precipAccumModel;

      if (modeChanged || hoursChanged || dayChanged || modelChanged) {
        this.currentMode = state.precipAccumMode || 'cumulative';
        this.currentHours = state.precipAccumHours || 24;
        this.currentDay = state.precipAccumDay || 1;
        this.currentModel = state.precipAccumModel || 'cmorph2';

        if (this.active) {
          this.refreshRaster();
        }
      }

      const isEnabled = state.precipAccumEnabled || state.primaryProduct === 'precip_accum' || state.primaryProduct === 'rain_accum' || !!(state.activeLayerProducts && (state.activeLayerProducts['precip_accum'] || state.activeLayerProducts['rain_accum']));
      if (isEnabled !== this.active) {
        if (isEnabled) {
          this.activate();
        } else {
          this.deactivate();
        }
      }
    });
  }

  protected onActivate(): void {
    this.refreshRaster();
  }

  protected onDeactivate(): void {
    this.layerManager.removeBufferedRasterLayer(this.id);
    this.layerManager.removeLayer(this.id);
  }

  public destroy(): void {
    this.onDeactivate();
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    if (this.active) {
      this.refreshRaster();
    }
  }

  public refresh(): void {
    this.refreshRaster();
  }

  private refreshRaster(): void {
    const state = store.getState();
    const manifest = state.manifest;
    if (!manifest) return;

    const opacity = getLayerOpacity(this.id, 1.0);
    const todayStr = new Date().toISOString().split('T')[0];
    let dateStr = state.currentTimeStep ? state.currentTimeStep.toISOString().split('T')[0] : todayStr;

    if (dateStr > todayStr) {
      dateStr = '';
    }

    const model = state.precipAccumModel || 'cmorph2';
    let intervalKey: string;
    let cleanDate = '';

    intervalKey = (state.precipAccumMode === 'daily') ? `day${state.precipAccumDay || 1}` : `${state.precipAccumHours || 24}h`;
    const availMap = (manifest as any)?.precip_accum?.available_by_model?.[model] || (manifest as any)?.precip_accum?.available?.[model];
    if (availMap && typeof availMap === 'object') {
      const dates = Object.keys(availMap).filter(d => d !== 'latest').sort();
      if (dates.length > 0 && dateStr && !dates.includes(dateStr)) {
        const pastDates = dates.filter(d => d <= dateStr);
        dateStr = pastDates.length > 0 ? pastDates[pastDates.length - 1] : dates[dates.length - 1];
      }
    }
    cleanDate = dateStr ? dateStr.replace(/-/g, '') : '';

    const tiles = [
      getTileUrl(manifest, this.id, model, cleanDate, intervalKey)
    ];

    this.layerManager.updateBufferedRasterLayer(this.id, tiles, opacity, undefined, 0, 6);
  }
}

export default PrecipAccumLayer;
